import React, { createContext, useState, useEffect, useCallback } from 'react';
import { AuthState, User, StripeDetails } from '../types';
import { useAuth0Service } from '../auth/auth0Service';
import { sessionStorage } from '../utils/sessionStorage';
import { apiClient } from '../api/client';

// Initial state
const initialState: AuthState = {
  currentUser: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isSessionExpired: false,
  isAdmin: false, // Add isAdmin property
  isUserDataLoading: false, // Add user data loading state
  userDataError: null, // Add user data error state
  isRetrying: false, // Add retry state
  retryAttempt: 0, // Add retry attempt counter
  maxRetries: 3, // Add maximum retry attempts
};

// Context
interface AuthContextType extends AuthState {
  login: (returnTo?: string, screenHint?: 'signup' | 'login') => Promise<void>;
  logout: () => void;
  getCurrentUser: () => User | null;
  clearError: () => void;
  handleSessionExpiration: () => void;
  dismissSessionExpiredModal: () => void;
  getToken: () => Promise<string>;
  refreshCurrentUser: () => Promise<void>;
  fetchUserRoles: () => Promise<void>;
  // Stripe onboarding methods
  getOnboardingStatus: () => Promise<{
    status: string;
    startedAt: string | null;
  }>;
  updateOnboardingStatus: (status: 'Incomplete' | 'InProgress' | 'Complete' | 'Redo') => Promise<void>;
  checkAbandonedOnboarding: () => Promise<{
    isAbandoned: boolean;
    status: string;
  }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  const auth0Service = useAuth0Service();
  
  // Update state when Auth0 state changes
  useEffect(() => {
    if (auth0Service.isLoading) {
      setState(prev => ({ ...prev, isLoading: true }));
      return;
    }
    
    const user = auth0Service.getCurrentUser();
    
    setState({
      currentUser: user,
      isAuthenticated: auth0Service.isAuthenticated,
      isAdmin: user?.isAdmin || false, // Add isAdmin property based on user
      isLoading: false,
      error: auth0Service.error ? new Error(auth0Service.error.message) : null,
      isSessionExpired: false,
      isUserDataLoading: false,
      userDataError: null,
      isRetrying: false,
      retryAttempt: 0,
      maxRetries: 3,
    });
    
    // Initialize token when authentication state changes and user is authenticated
    if (auth0Service.isAuthenticated) {
      console.log('AuthContext: User authenticated, initializing token');
      auth0Service.initializeTokens().catch(error => {
        console.error('AuthContext: Error initializing token:', error);
      });
      
      // Fetch complete user data from the backend API
      refreshCurrentUserFromAPI();
      
      // Fetch user roles
      fetchUserRoles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth0Service.isAuthenticated,
    auth0Service.isLoading,
    auth0Service.error
    // Removed auth0Service from the dependency array to fix the infinite loop
  ]);
  
  // Handle session expiration
  const handleSessionExpiration = useCallback((): void => {
    setState(prev => ({
      ...prev,
      isAuthenticated: false,
      currentUser: null,
      isAdmin: false, // Reset admin flag when session expires
      isRetrying: false, // Reset retry state when session expires
      retryAttempt: 0, // Reset retry attempt counter
      isSessionExpired: true,
      isUserDataLoading: false,
      userDataError: null,
    }));
  }, []);
  
  // Listen for session expired events
  useEffect(() => {
    const handleSessionExpiredEvent = () => {
      handleSessionExpiration();
    };
    
    window.addEventListener('session-expired', handleSessionExpiredEvent);
    
    return () => {
      window.removeEventListener('session-expired', handleSessionExpiredEvent);
    };
  }, [handleSessionExpiration]);
  
  // Login
  const login = async (returnTo?: string, screenHint?: 'signup' | 'login'): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      // Clear any existing session data before login to ensure a clean slate
      sessionStorage.clearAllAppData();
      await auth0Service.login(returnTo, screenHint);
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error as Error,
        isUserDataLoading: false,
        userDataError: null,
      }));
      throw error;
    }
  };
  
  // Logout
  const logout = (): void => {
    setState(prev => ({ ...prev, isLoading: true }));
    sessionStorage.clearAllAppData();
    auth0Service.logout();
  };
  
  // Get current user
  const getCurrentUser = (): User | null => {
    return state.currentUser;
  };
  
  // Get token
  const getToken = async (): Promise<string> => {
    console.log('AuthContext: Getting token from auth0Service');
    try {
      const token = await auth0Service.getToken();
      console.log('AuthContext: Token received:', token ? 'Token exists' : 'No token received');
      return token || '';
    } catch (error) {
      console.error('AuthContext: Error getting token:', error);
      throw error;
    }
  };
  
  // Refresh current user data from the backend API with retry logic
  const refreshCurrentUserFromAPI = async (retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const BASE_DELAY = 1000; // 1 second base delay
    
    if (!auth0Service.isAuthenticated) {
      console.log('AuthContext: User not authenticated, skipping user data refresh');
      return;
    }
    
    try {
      console.log(`AuthContext: Fetching user data from API (attempt ${retryCount + 1}/${MAX_RETRIES})`);
      
      setState(prev => ({
        ...prev,
        isUserDataLoading: true,
        userDataError: null,
        isRetrying: retryCount > 0,
        retryAttempt: retryCount,
        maxRetries: MAX_RETRIES
      }));
      
      // Get user data from API
      const apiUserData = await apiClient.auth.getCurrentUser();
      console.log('AuthContext: User data received from API:', apiUserData);
      
      // Get user data from Auth0
      const auth0User = auth0Service.getCurrentUser();
      
      // Create a properly typed user object from the API response
      const apiUser: Partial<User> = {};
      
      // Safely extract properties from the API response
      if (apiUserData && typeof apiUserData === 'object') {
        const data = apiUserData as Record<string, any>;
        
        if (typeof data.id === 'string') apiUser.id = data.id;
        if (typeof data.email === 'string') apiUser.email = data.email;
        if (typeof data.name === 'string') apiUser.name = data.name;
        if (typeof data.userId === 'string') apiUser.userId = data.userId;
        if (typeof data.hasSetUserId === 'boolean') apiUser.hasSetUserId = data.hasSetUserId;
        if (typeof data.avatar === 'string') apiUser.avatar = data.avatar;
        if (typeof data.rating === 'number') apiUser.rating = data.rating;
        if (typeof data.createdAt === 'string') apiUser.createdAt = data.createdAt;
        if (typeof data.updatedAt === 'string') apiUser.updatedAt = data.updatedAt;
        
        // Extract stripeDetails if it exists
        if (data.stripeDetails && typeof data.stripeDetails === 'object') {
          apiUser.stripeDetails = data.stripeDetails as StripeDetails;
        }
      }
      
      // Merge Auth0 user data with backend user data
      const mergedUser: User = {
        id: apiUser.id || auth0User?.id || '',
        email: apiUser.email || auth0User?.email || '',
        name: apiUser.name || auth0User?.name || '',
        hasSetUserId: apiUser.hasSetUserId || false,
        userId: apiUser.userId,
        avatar: apiUser.avatar,
        rating: apiUser.rating,
        isAdmin: auth0User?.isAdmin || false, // Preserve isAdmin flag from Auth0
        createdAt: apiUser.createdAt || auth0User?.createdAt || new Date().toISOString(),
        updatedAt: apiUser.updatedAt || auth0User?.updatedAt || new Date().toISOString(),
        stripeDetails: apiUser.stripeDetails,
      };
      
      console.log('AuthContext: Merged user data:', mergedUser);
      
      // Success - reset retry state
      setState(prev => ({
        ...prev,
        currentUser: mergedUser,
        isAdmin: mergedUser.isAdmin || false, // Update isAdmin in state
        isUserDataLoading: false,
        userDataError: null,
        isRetrying: false,
        retryAttempt: 0,
        maxRetries: MAX_RETRIES
      }));
      
      console.log('AuthContext: User data updated successfully');
    } catch (error) {
      console.error(`AuthContext: Error fetching user data (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error);
      
      if (retryCount < MAX_RETRIES - 1) {
        // Calculate delay with exponential backoff + jitter
        const baseDelay = BASE_DELAY * Math.pow(2, retryCount);
        const jitter = Math.random() * 500; // 0-500ms jitter to prevent thundering herd
        const delay = baseDelay + jitter;
        
        console.log(`AuthContext: Retrying in ${Math.round(delay)}ms (attempt ${retryCount + 2}/${MAX_RETRIES})`);
        
        setTimeout(() => {
          refreshCurrentUserFromAPI(retryCount + 1);
        }, delay);
        
        return; // Don't set error state yet - we're retrying
      }
      
      // All retries exhausted - set error state
      console.error('AuthContext: All retry attempts exhausted, setting error state');
      setState(prev => ({
        ...prev,
        isUserDataLoading: false,
        userDataError: error as Error,
        isRetrying: false,
        retryAttempt: 0,
        maxRetries: MAX_RETRIES
      }));
    }
  };
  
  // Public method to refresh current user data
  const refreshCurrentUser = async (): Promise<void> => {
    return refreshCurrentUserFromAPI();
  };
  
  // Get onboarding status
  const getOnboardingStatus = async (): Promise<{
    status: string;
    startedAt: string | null;
  }> => {
    if (!auth0Service.isAuthenticated) {
      console.log('AuthContext: User not authenticated, skipping onboarding status check');
      return { status: 'Incomplete', startedAt: null };
    }
    
    // First check if we already have the status in the current user's stripeDetails
    if (state.currentUser?.stripeDetails) {
      const { onboardingStatus, onboardingStartedAt } = state.currentUser.stripeDetails;
      console.log('AuthContext: Using cached onboarding status:', onboardingStatus);
      return {
        status: onboardingStatus,
        startedAt: onboardingStartedAt || null
      };
    }
    
    try {
      console.log('AuthContext: Fetching onboarding status from API');
      const response = await apiClient.stripe.getOnboardingStatus();
      console.log('AuthContext: Onboarding status received from API:', response);
      
      // Update the stripeDetails in the current user with the fetched status
      if (state.currentUser) {
        setState(prev => {
          if (!prev.currentUser) return prev;
          
          // Create or update stripeDetails with the fetched status
          const updatedStripeDetails: StripeDetails = prev.currentUser.stripeDetails
            ? {
                ...prev.currentUser.stripeDetails,
                onboardingStatus: response.status as 'Incomplete' | 'InProgress' | 'Complete' | 'Redo',
                onboardingStartedAt: response.startedAt || undefined
              }
            : {
                id: '', // This will be replaced by the backend
                userId: prev.currentUser.id,
                stripeConnectedAccountId: '',
                onboardingStatus: response.status as 'Incomplete' | 'InProgress' | 'Complete' | 'Redo',
                onboardingStartedAt: response.startedAt || undefined,
                verificationStatus: 'NotRequired',
                verificationRequirements: []
              };
          
          return {
            ...prev,
            currentUser: {
              ...prev.currentUser,
              stripeDetails: updatedStripeDetails
            }
          };
        });
      }
      
      return response;
    } catch (error) {
      console.error('AuthContext: Error fetching onboarding status:', error);
      return { status: 'Incomplete', startedAt: null };
    }
  };
  
  // Update onboarding status
  const updateOnboardingStatus = async (status: 'Incomplete' | 'InProgress' | 'Complete' | 'Redo'): Promise<void> => {
    if (!auth0Service.isAuthenticated) {
      console.log('AuthContext: User not authenticated, skipping onboarding status update');
      return;
    }
    
    try {
      console.log(`AuthContext: Updating onboarding status to ${status}`);
      await apiClient.stripe.updateOnboardingStatus(status);
      console.log('AuthContext: Onboarding status updated successfully');
      
      // Update the user object with the new status in stripeDetails
      setState(prev => {
        if (!prev.currentUser) return prev;
        
        // Create a new stripeDetails object with updated status
        const updatedStripeDetails: StripeDetails = prev.currentUser.stripeDetails
          ? {
              ...prev.currentUser.stripeDetails,
              onboardingStatus: status,
              onboardingStartedAt: status === 'InProgress'
                ? new Date().toISOString()
                : prev.currentUser.stripeDetails.onboardingStartedAt
            }
          : {
              id: '', // This will be replaced by the backend
              userId: prev.currentUser.id,
              stripeConnectedAccountId: '',
              onboardingStatus: status,
              onboardingStartedAt: status === 'InProgress' ? new Date().toISOString() : undefined,
              verificationStatus: 'NotRequired',
              verificationRequirements: []
            };
        
        return {
          ...prev,
          currentUser: {
            ...prev.currentUser,
            stripeDetails: updatedStripeDetails
          }
        };
      });
    } catch (error) {
      console.error('AuthContext: Error updating onboarding status:', error);
    }
  };
  
  // Check for abandoned onboarding
  const checkAbandonedOnboarding = async (): Promise<{
    isAbandoned: boolean;
    status: string;
  }> => {
    if (!auth0Service.isAuthenticated) {
      console.log('AuthContext: User not authenticated, skipping abandoned onboarding check');
      return { isAbandoned: false, status: 'Incomplete' };
    }
    
    try {
      console.log('AuthContext: Checking for abandoned onboarding');
      const response = await apiClient.stripe.checkAbandonedOnboarding();
      console.log('AuthContext: Abandoned onboarding check result:', response);
      
      // Update the user object if the status has changed
      if (response.isAbandoned &&
          state.currentUser?.stripeDetails?.onboardingStatus !== response.status) {
        setState(prev => {
          if (!prev.currentUser) return prev;
          
          // Create updated stripeDetails with new status
          const updatedStripeDetails: StripeDetails = prev.currentUser.stripeDetails
            ? {
                ...prev.currentUser.stripeDetails,
                onboardingStatus: response.status as 'Incomplete' | 'InProgress' | 'Complete' | 'Redo',
                onboardingStartedAt: undefined
              }
            : {
                id: '', // This will be replaced by the backend
                userId: prev.currentUser.id,
                stripeConnectedAccountId: '',
                onboardingStatus: response.status as 'Incomplete' | 'InProgress' | 'Complete' | 'Redo',
                onboardingStartedAt: undefined,
                verificationStatus: 'NotRequired',
                verificationRequirements: []
              };
          
          return {
            ...prev,
            currentUser: {
              ...prev.currentUser,
              stripeDetails: updatedStripeDetails
            }
          };
        });
      }
      
      return response;
    } catch (error) {
      console.error('AuthContext: Error checking abandoned onboarding:', error);
      return { isAbandoned: false, status: 'Incomplete' };
    }
  };
  
  // Fetch user roles from the backend API
  const fetchUserRoles = async (): Promise<void> => {
    if (!auth0Service.isAuthenticated) {
      console.log('AuthContext: User not authenticated, skipping roles fetch');
      return;
    }
    
    try {
      console.log('AuthContext: Fetching user roles');
      const roles = await auth0Service.getUserRoles();
      console.log('AuthContext: User roles received:', roles);
      
      // Check if user has admin role
      const isAdmin = roles.includes('app-admin');
      
      setState(prev => ({
        ...prev,
        isAdmin
      }));
      
      console.log('AuthContext: isAdmin set to', isAdmin);
    } catch (error) {
      console.error('AuthContext: Error fetching user roles:', error);
    }
  };
  
  // Clear error
  const clearError = (): void => {
    setState(prev => ({ ...prev, error: null, userDataError: null }));
  };
  
  // Dismiss session expired modal
  const dismissSessionExpiredModal = (): void => {
    setState(prev => ({ ...prev, isSessionExpired: false }));
  };
  
  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        getCurrentUser,
        clearError,
        handleSessionExpiration,
        dismissSessionExpiredModal,
        getToken,
        refreshCurrentUser,
        fetchUserRoles,
        // Stripe onboarding methods
        getOnboardingStatus,
        updateOnboardingStatus,
        checkAbandonedOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};